import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { inviteApi } from '@/api/auth';
import { useAuthStore } from '@/store/auth';

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const token = searchParams.get('token') || '';

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid invitation link. No token found.');
      return;
    }

    if (fullName.trim().length < 2) {
      setError('Full name must be at least 2 characters.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const data = await inviteApi.accept(token, fullName.trim(), password);

      // Auto-login
      login(
        {
          id: data.user.id,
          client_id: data.user.client_id,
          email: data.user.email,
          full_name: data.user.full_name,
          role: data.user.role as 'admin' | 'analyst' | 'viewer',
          is_active: true,
          last_login: null,
        },
        data.access_token,
        data.refresh_token
      );

      // Small delay for store persistence
      await new Promise(resolve => setTimeout(resolve, 300));
      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to accept invitation. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mono-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-2">
            <img src="/argos-logo.png" alt="ARGOS" className="h-10 w-auto object-contain" />
          </div>
          <p className="text-[10px] text-mono-500 tracking-[0.3em]">ACCEPT INVITATION</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-mono-900 border border-mono-800 rounded-lg p-6 space-y-4">
          <p className="text-xs text-mono-400 tracking-wider mb-4">
            CREATE YOUR ACCOUNT TO JOIN THE TEAM
          </p>

          {error && (
            <div className="border border-red-500/30 bg-red-500/10 p-3 rounded">
              <p className="text-red-400 text-xs tracking-wider">{error}</p>
            </div>
          )}

          {!token && (
            <div className="border border-amber-500/30 bg-amber-500/10 p-3 rounded">
              <p className="text-amber-400 text-xs tracking-wider">
                NO INVITATION TOKEN FOUND. PLEASE USE THE LINK FROM YOUR INVITATION EMAIL.
              </p>
            </div>
          )}

          <div>
            <label className="block text-[10px] text-mono-500 tracking-widest mb-1">FULL NAME</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Smith"
              className="w-full bg-mono-800 border border-mono-700 text-mono-200 text-xs rounded px-3 py-2.5 tracking-wider placeholder-mono-600 focus:outline-none focus:border-cyan-600"
              required
            />
          </div>

          <div>
            <label className="block text-[10px] text-mono-500 tracking-widest mb-1">PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 8 characters"
              className="w-full bg-mono-800 border border-mono-700 text-mono-200 text-xs rounded px-3 py-2.5 tracking-wider placeholder-mono-600 focus:outline-none focus:border-cyan-600"
              required
            />
          </div>

          <div>
            <label className="block text-[10px] text-mono-500 tracking-widest mb-1">CONFIRM PASSWORD</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              className="w-full bg-mono-800 border border-mono-700 text-mono-200 text-xs rounded px-3 py-2.5 tracking-wider placeholder-mono-600 focus:outline-none focus:border-cyan-600"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading || !token}
            className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-xs tracking-widest font-medium rounded transition-colors"
          >
            {loading ? 'CREATING ACCOUNT...' : 'JOIN TEAM'}
          </button>

          <div className="text-center pt-2 space-y-2">
            <Link
              to="/login"
              className="block text-[10px] text-mono-500 hover:text-cyan-400 tracking-widest transition-colors"
            >
              ALREADY HAVE AN ACCOUNT? LOGIN
            </Link>
            <Link
              to="/employee/signup"
              className="block text-[10px] text-mono-500 hover:text-cyan-400 tracking-widest transition-colors"
            >
              HAVE AN ORGANIZATION CODE INSTEAD? SIGN UP HERE
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
